import axios from 'axios';

// Interface definitions
export interface Workflow {
    id: string;
    name: string;
    nameSlug?: string;
    description?: string;
    status: 'NOTDEPLOYED' | 'DEPLOYED' | 'PAUSED';
    definitionId?: string;
    definitionKey?: string;
    version?: string;
    createdDate?: string;
    modifiedDate?: string;
    triggerType?: string;
    lastModifiedDate?: string;
    initialBpmn?: string; // Base64 encoded initial BPMN from UI
}

export interface WorkflowContent {
    id: string;
    nameSlug: string;
    version: string;
    bpmn: string; // Base64 encoded
    bpmnContentType: string;
    process: Workflow;
}

export interface WorkflowListResponse {
    content: Workflow[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

// Connector property from template
export interface ConnectorProperty {
    id: string;
    label: string;
    type: 'string' | 'number' | 'boolean' | 'json' | 'select' | 'wysiwyg' | 'mapped-json';
    required?: boolean;
    placeholder?: string;
    /**
     * Persistent help text rendered under the field's label. Use for prerequisites
     * or contract notes that should remain visible after the user starts typing
     * (placeholders disappear once a value is entered).
     */
    description?: string;
    default?: string;
    parameterType?: 'input' | 'output' | 'property';
    options?: { label: string; value: string }[];
    hidden?: boolean;
    /**
     * Conditional visibility: this property is only rendered when the value of
     * {@code field} (looked up by property id) is contained in {@code value}.
     * Honored by CustomPropertiesPanel.
     */
    showWhen?: {
        field: string;
        value: string[];
    };
    /**
     * When set, edits to THIS property cause CustomPropertiesPanel to
     * recompute the property whose id is `targetProperty`. The target
     * is expected to declare a {@link template} so its value can be
     * interpolated from sibling properties.
     *
     * Used to drive composite inputs — e.g. a hidden `payload` field
     * whose value is built from several user-facing inputs like
     * `sourceDocument`, `instructionPrompt`, `advancedOCR`.
     */
    combineInto?: {
        targetProperty: string;
    };
    /**
     * Mustache-lite template string with `{{propertyId}}` placeholders.
     * Each placeholder is substituted with that sibling property's
     * current value (or its `default`, or empty) when the panel
     * interpolates. Typically used together with sibling properties
     * that declare `combineInto` pointing at this property.
     *
     * Example:
     *   template: "{ \"prompt\": \"{{instructionPrompt}}\" }"
     */
    template?: string;
}

// Designer template from API
export interface DesignerTemplate {
    id: string;
    name: string;
    category: string;
    type: string;
    jsonContent: string;
    description?: string;
    icon?: string;
    // Decoded fields from jsonContent
    decodedContent?: {
        type: string;
        name: string;
        icon: string;
        color: string;
        modelerTemplateIcon?: string;
        implementation?: string;
        topic?: string;
        properties?: ConnectorProperty[];
    };
}


// Get auth token from localStorage
const getAuthToken = (): string => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create API instance
const designerApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/designer/api' : '/designer/api',
});

// Add request interceptor for auth token
designerApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
designerApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Unauthorized - token may have expired');
        }
        return Promise.reject(error);
    }
);

// Create axios instance for Engine REST API (deployments)
const engineApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/engine-rest' : '/engine/engine-rest',
});

// Add auth interceptor to engine API as well
engineApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ============================================================================
// Workflow CRUD Operations
// ============================================================================

/**
 * Get paginated list of workflows
 */
export const getWorkflows = async (
    page: number = 0,
    size: number = 20,
    search?: string
): Promise<WorkflowListResponse> => {
    let url = `/processes?page=${page}&size=${size}&sort=createdDate,DESC`;
    if (search) {
        url += `&search=${encodeURIComponent(search)}`;
    }
    const response = await designerApi.get<WorkflowListResponse>(url);
    return response.data;
};

/**
 * Get a single workflow by ID
 */
export const getWorkflow = async (id: string): Promise<Workflow> => {
    const response = await designerApi.get<Workflow>(`/processes/${id}`);
    return response.data;
};

/**
 * Create a new workflow
 * If initialBpmn is provided in data (e.g., from a template), it will be used.
 * Otherwise, generates BPMN with process ID in format: nameSlug_randomId
 */
export const createWorkflow = async (data: Partial<Workflow>): Promise<Workflow> => {
    const nameSlug = data.nameSlug || (data.name || 'workflow').toLowerCase().replace(/\s+/g, '_');
    const version = data.version || '0.1';
    const triggerType = data.triggerType || 'Manual';

    // Use provided initialBpmn (from template) or generate a new one
    let initialBpmnBase64: string;
    if (data.initialBpmn) {
        // If initialBpmn is already provided (e.g., from template), use it as-is
        // Check if it's already base64 encoded or needs encoding
        try {
            atob(data.initialBpmn); // Test if it's valid base64
            initialBpmnBase64 = data.initialBpmn;
        } catch {
            // Not base64, encode it
            initialBpmnBase64 = encodeBpmnXML(data.initialBpmn);
        }
    } else {
        // Generate initial BPMN with proper process ID and trigger-based start event
        const initialBpmnXml = generateBpmnTemplate(nameSlug, version, triggerType);
        initialBpmnBase64 = encodeBpmnXML(initialBpmnXml);
    }

    const payload = {
        ...data,
        nameSlug,
        status: 'NOTDEPLOYED',
        definitionId: null,
        definitionKey: null,
        initialBpmn: initialBpmnBase64,
    };
    const response = await designerApi.post<Workflow>('/processes', payload);
    return response.data;
};

/**
 * Update a workflow
 */
export const updateWorkflow = async (id: string, data: Partial<Workflow>): Promise<Workflow> => {
    // Backend requires id in the request body
    const response = await designerApi.patch<Workflow>(`/processes/${id}`, { id, ...data });
    return response.data;
};

/**
 * Delete a workflow
 */
export const deleteWorkflow = async (id: string): Promise<void> => {
    await designerApi.delete(`/processes/${id}`);
};

// ============================================================================
// Workflow Content (BPMN) Operations
// ============================================================================

/**
 * Get workflow BPMN content by slug
 */
export const getWorkflowContent = async (nameSlug: string): Promise<WorkflowContent> => {
    const response = await designerApi.get<WorkflowContent>(`/process-contents/byslug/${nameSlug}`);
    return response.data;
};

/**
 * Get workflow BPMN content by slug and specific version
 */
export const getWorkflowContentByVersion = async (nameSlug: string, version: string): Promise<WorkflowContent> => {
    const response = await designerApi.get<WorkflowContent>(`/process-contents/${nameSlug}/${version}`);
    return response.data;
};

/**
 * Decode BPMN XML from base64
 */
export const decodeBpmnXML = (base64String: string): string => {
    try {
        return atob(base64String);
    } catch (error) {
        console.error('Error decoding BPMN XML:', error);
        throw new Error('Invalid BPMN XML encoding');
    }
};

/**
 * Encode BPMN XML to base64
 */
export const encodeBpmnXML = (xml: string): string => {
    try {
        return btoa(xml);
    } catch (error) {
        console.error('Error encoding BPMN XML:', error);
        throw new Error('Failed to encode BPMN XML');
    }
};

/**
 * Save workflow BPMN content
 */
export const saveWorkflowContent = async (
    nameSlug: string,
    version: string,
    bpmnXML: string,
    process?: Partial<Workflow>
): Promise<WorkflowContent> => {
    const payload = {
        id: null,
        nameSlug,
        version,
        bpmn: encodeBpmnXML(bpmnXML),
        bpmnContentType: 'text/xml',
        process: process || {},
    };
    const response = await designerApi.post<WorkflowContent>('/process-contents', payload);

    // Also update the process with the new version
    if (response.data.process?.id) {
        await designerApi.patch(`/processes/${response.data.process.id}`, {
            ...response.data.process,
            version,
        });
    }

    return response.data;
};

// ============================================================================
// Designer Templates
// ============================================================================

/**
 * Get designer templates for connectors
 */
export const getDesignerTemplates = async (): Promise<DesignerTemplate[]> => {
    const response = await designerApi.get<DesignerTemplate[]>('/templates');

    // Decode the jsonContent for each template
    const decodedTemplates = response.data.map(template => {
        try {
            const decoded = JSON.parse(atob(template.jsonContent));
            return {
                ...template,
                decodedContent: decoded
            };
        } catch {
            console.warn(`Failed to decode jsonContent for template: ${template.name}`);
            return template;
        }
    });

    return decodedTemplates;
};

/**
 * Save a designer template
 */
export const saveDesignerTemplate = async (template: Partial<DesignerTemplate>): Promise<DesignerTemplate> => {
    // If jsonContent is not set but properties are, we might need to construct it?
    // The backend purely saves what we send.
    // The user provided JSON structure implies the "jsonContent" field in the DB works like a blob, 
    // OR the controller expects the fields expanded.
    // Looking at ElementTemplate.java (implied), it likely has these fields as columns or a json column.
    // But the React app handles `jsonContent` as a base64 encoded string in `getDesignerTemplates`.
    // If the controller accepts the object as provided in the prompt, then we should send that object.
    // However, the `DesignerTemplate` interface has `jsonContent: string`.
    // If we are building it, we probably construct the JSON, stringify it, base64 encode it, and send it as `jsonContent`.
    // OR, we send the fields and the backend handles it.
    // Let's assume for Build Mode, we treat the whole detailed object (properties etc) as the "content" 
    // and wrap it into the `jsonContent` string for storage, OR the backend unwraps it.

    // Actually, looking at the user request: "here is the json for the building connector templare... we need to build the screens fields and save".
    // And the `ElementTemplateController` `save` takes `@RequestBody ElementTemplate template`.
    // If `ElementTemplate` has `properties` list directly, then we don't need `jsonContent`.
    // But `DesignerTemplate` interface in FE has `jsonContent`. 
    // Let's assume we map our builder state to the `ElementTemplate` structure.
    // If the `DesignerTemplate` interface on FE doesn't match the Backend exactly (e.g. backend has structured fields, FE sees a blob), 
    // we might need to adjust.
    // BUT `getDesignerTemplates` decodes `jsonContent`.
    // This implies the backend stores it as a blob/string.
    // So we should Base64 encode our built object and send it as `jsonContent`.
    // Wait, the user sample JSON HAS `properties` inside it.
    // So the "Template" object has a field `jsonContent` which contains... the `properties`?
    // The user sample starts with `type`, `name`... and `properties`.
    // This looks like the CONTENT of the template.
    // So we wrap this whole object into `jsonContent`.

    // Construct the payload
    const payload = {
        ...template,
        // Ensure jsonContent is populated if we are sending from builder
        jsonContent: template.jsonContent || btoa(JSON.stringify(template.decodedContent || {}))
    };

    const response = await designerApi.post<DesignerTemplate>('/templates', payload);
    return response.data;
};

// Generate a random alphanumeric ID (8 characters)
const generateRandomId = (): string => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

/**
 * Generate BPMN template with dynamic process ID, version, and trigger-based start event
 * Process ID format: nameSlug_randomId (e.g., my_workflow_abc12345)
 * 
 * Trigger types:
 * - Manual / AI: Regular Start Event
 * - Timer: Timer Start Event with cron expression
 * - Automatic: Message Start Event
 */
export const generateBpmnTemplate = (
    nameSlug: string,
    version: string = '0.1',
    triggerType: string = 'Manual',
    cronExpression: string = '0 0 * * * ?'
): string => {
    const processId = `${nameSlug}_${generateRandomId()}`;

    // Generate start event based on trigger type
    let startEventXml: string;
    let startEventName: string;

    switch (triggerType?.toLowerCase()) {
        case 'timer':
            // Timer Start Event with cron expression
            startEventName = 'Timer Start';
            startEventXml = `<bpmn2:startEvent id="StartEvent_1" name="${startEventName}">
      <bpmn2:timerEventDefinition id="TimerEventDef_1">
        <bpmn2:timeCycle xsi:type="bpmn2:tFormalExpression">${cronExpression}</bpmn2:timeCycle>
      </bpmn2:timerEventDefinition>
    </bpmn2:startEvent>`;
            break;

        case 'automatic':
            // Message Start Event
            startEventName = 'Message Start';
            startEventXml = `<bpmn2:startEvent id="StartEvent_1" name="${startEventName}">
      <bpmn2:messageEventDefinition id="MessageEventDef_1" messageRef="Message_${generateRandomId()}" />
    </bpmn2:startEvent>`;
            break;

        case 'manual':
        case 'ai':
        default:
            // Regular Start Event
            startEventName = 'Start';
            startEventXml = `<bpmn2:startEvent id="StartEvent_1" name="${startEventName}" />`;
            break;
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<bpmn2:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bpmn2="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" xmlns:camunda="http://camunda.org/schema/1.0/bpmn" id="sample-diagram" targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn2:process id="${processId}" name="${nameSlug}" isExecutable="true" camunda:versionTag="${version}" camunda:historyTimeToLive="2">
    ${startEventXml}
  </bpmn2:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="${processId}">
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_2" bpmnElement="StartEvent_1">
        <dc:Bounds x="152" y="102" width="36" height="36" />
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn2:definitions>`
};

// Empty BPMN XML template for new workflows (legacy - use generateBpmnTemplate for new workflows)
export const emptyBpmnTemplate = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn2:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bpmn2="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" xmlns:camunda="http://camunda.org/schema/1.0/bpmn" id="sample-diagram" targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn2:process id="Process_1" isExecutable="true" camunda:versionTag="0.1" camunda:historyTimeToLive="2">
    <bpmn2:startEvent id="StartEvent_1" />
  </bpmn2:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_2" bpmnElement="StartEvent_1">
        <dc:Bounds x="152" y="102" width="36" height="36" />
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn2:definitions>`;

/**
 * Get all versions of a workflow by slug
 */
export const getWorkflowVersions = async (nameSlug: string): Promise<WorkflowContent[]> => {
    // Use the new /versions endpoint that returns all versions as a list
    const response = await designerApi.get<WorkflowContent[]>(`/process-contents/byslug/${nameSlug}/versions`);
    return Array.isArray(response.data) ? response.data : (response.data as any).content || [];
};

/**
 * Deploy workflow BPMN
 * @param nameSlug - The workflow name slug
 * @param bpmnXML - The BPMN XML content
 * @param version - The workflow version (optional, will be included in deployment name)
 * @param source - Deployment source identifier
 */
export const deployWorkflow = async (
    nameSlug: string,
    bpmnXML: string,
    version?: string,
    source: string = 'doit-studio'
): Promise<any> => {
    const formData = new FormData();

    // Include version in deployment name if provided (e.g., "my_workflow_v0.1")
    const deploymentName = version ? `${nameSlug}_v${version}` : nameSlug;
    formData.append('deployment-name', deploymentName);
    formData.append('deployment-source', source);
    formData.append('deploy-changed-only', 'true');

    // Create a blob for the file
    const blob = new Blob([bpmnXML], { type: 'text/xml' });
    formData.append('file', blob, `${nameSlug}.bpmn`);

    const response = await engineApi.post('/deployment/create', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

/**
 * Get all deployments for a workflow by nameSlug (deployment name)
 */
export const getDeploymentsByNameSlug = async (nameSlug: string): Promise<any[]> => {
    const response = await engineApi.get(`/deployment?name=${nameSlug}`);
    return response.data || [];
};

/**
 * Get count of running process instances for a process definition
 */
export const getRunningInstancesCount = async (processDefinitionKey: string): Promise<number> => {
    try {
        const response = await engineApi.get(`/process-instance/count?processDefinitionKey=${processDefinitionKey}`);
        return response.data?.count || 0;
    } catch (error) {
        console.error('Error getting running instances count:', error);
        return 0;
    }
};

/**
 * Delete a deployment by ID
 * @param deploymentId - The deployment ID to delete
 * @param cascade - If true, also deletes running instances and history
 */
export const deleteDeployment = async (deploymentId: string, cascade: boolean = true): Promise<void> => {
    await engineApi.delete(`/deployment/${deploymentId}?cascade=${cascade}&skipCustomListeners=true&skipIoMappings=true`);
};

export default {
    getWorkflows,
    getWorkflow,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    getWorkflowContent,
    saveWorkflowContent,
    getDesignerTemplates,
    decodeBpmnXML,
    encodeBpmnXML,
    emptyBpmnTemplate,
};
